import { useState } from 'react';
import { ApplicationView as View, showValue } from '../../services/reviewApi';
import { dateShort } from '../../lib/format';
import { Field, SectionTitle } from './parts';

const PRODUCTIVITY = [
  'papers_first_author',
  'papers_other_author',
  'posters_first_author',
  'posters_other_author',
  'kmb_activities',
];

export const extraAnchor = (id: string) => `answer-${id}`;

function Attachments({
  view,
  onDownload,
}: {
  view: View;
  onDownload?: (fieldId: string, label: string) => Promise<void>;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [missing, setMissing] = useState<Record<string, string>>({});
  const files = view.attachments ?? [];
  if (!files.length || !onDownload) return null;
  const open = async (fieldId: string, label: string) => {
    setBusy(fieldId);
    try {
      await onDownload(fieldId, label);
      setMissing((m) => ({ ...m, [fieldId]: '' }));
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status;
      setMissing((m) => ({ ...m, [fieldId]: status === 404 ? 'No file uploaded' : 'Could not open the file' }));
    } finally {
      setBusy(null);
    }
  };
  return (
    <div className="mt-3">
      <div className="text-small text-ink-2 mb-1">Attachments</div>
      {files.map((f) => (
        <div key={f.fieldId} className="flex items-center gap-3 py-1 text-table">
          <button
            type="button"
            className="underline disabled:opacity-50"
            disabled={busy === f.fieldId}
            onClick={() => open(f.fieldId, f.label)}
          >
            {busy === f.fieldId ? 'Opening…' : f.label}
          </button>
          {missing[f.fieldId] ? <span className="text-small text-ink-2">{missing[f.fieldId]}</span> : null}
        </div>
      ))}
      <p className="text-small text-ink-2 mt-1">Each download is recorded. Do not forward or store these files.</p>
    </div>
  );
}

export default function ApplicationView({
  view,
  onDownload,
}: {
  view: View;
  onDownload?: (fieldId: string, label: string) => Promise<void>;
}) {
  const id = view.identity;
  if (!id) return null;
  const answers = view.substance?.answers ?? [];
  const counts = answers.filter((a) => PRODUCTIVITY.includes(a.key));
  const rest = answers.filter((a) => !PRODUCTIVITY.includes(a.key) && a.key !== 'took_leave');
  const leave = view.substance?.leave;

  return (
    <div>
      <SectionTitle>Applicant</SectionTitle>
      <Field label="Name" value={id.name} />
      <Field label="Institution" value={id.institution} />
      <Field label="Career stage" value={id.degreeLevel ?? id.careerStage} />
      <Field
        label="Discipline"
        value={id.disciplineOther ? `${id.discipline} (${id.disciplineOther})` : id.discipline}
      />
      <Field label="Region" value={id.region} />
      <Field label="Supervisor" value={id.supervisorName} />
      <Field label="Supervisor is a DIVERT PI or Co-I" value={id.supervisorIsInvestigator} />
      <Field label="Preferred language" value={id.preferredLanguage} />
      {id.pronouns ? <Field label="Pronouns" value={id.pronouns} /> : null}
      <Field label="Submitted" value={dateShort(id.submittedAt)}>
        {dateShort(id.submittedAt)}
        {id.late ? <span className="text-warning"> · after intake closed</span> : null}
      </Field>

      {view.eligibility ? (
        <>
          <SectionTitle note={view.eligibility.version ? `rules v${view.eligibility.version}` : undefined}>
            Eligibility
          </SectionTitle>
          <Field label="Status" value={view.eligibility.status} />
          {view.eligibility.flags.map((f, i) => (
            <Field key={i} label={f.outcome === 'fail' ? 'Does not meet' : 'Needs a check'} value={f.message} />
          ))}
          {view.eligibility.note ? <Field label="Staff note" value={view.eligibility.note} /> : null}
        </>
      ) : null}

      {view.substance ? (
        <>
          <SectionTitle>Productivity</SectionTitle>
          {counts.map((a) => (
            <Field key={a.key} label={a.label} value={a.value} />
          ))}
          <Field label="Took an official leave" value={leave?.tookLeave}>
            {showValue(leave?.tookLeave)}
            {leave?.detail && !/^n\/?a$/i.test(showValue(leave.detail).trim()) ? (
              <span className="text-ink-2"> · {showValue(leave.detail)}</span>
            ) : null}
          </Field>

          <SectionTitle>Application</SectionTitle>
          {view.substance.extra.map((e) => (
            <div key={e.id} id={extraAnchor(e.id)} className="scroll-mt-24">
              <Field label={e.label} value={e.value} />
            </div>
          ))}
          {rest.map((a) => (
            <Field key={a.key} label={a.label} value={a.value} />
          ))}
          <Attachments view={view} onDownload={onDownload} />
        </>
      ) : null}

      {view.selfId ? (
        <>
          <SectionTitle note="Shown for the committee's deliberation; never used in any score or ranking">
            Self-identification
          </SectionTitle>
          {view.selfId.answers.map((a) => (
            <Field key={a.key} label={a.label} value={a.value} />
          ))}
          {view.selfId.extra.map((e) => (
            <Field key={e.id} label={e.label} value={e.value} />
          ))}
          {!view.selfId.answers.length && !view.selfId.extra.length ? (
            <p className="text-small text-ink-2">No self-identification answers.</p>
          ) : null}
        </>
      ) : null}

      {view.contact?.length ? (
        <>
          <SectionTitle note="Program Manager only">Contact</SectionTitle>
          <Field label="Email" value={id.email} />
          {view.contact.map((e) => (
            <Field key={e.id} label={e.label} value={e.value} />
          ))}
        </>
      ) : null}
    </div>
  );
}
